import i18next from 'i18next';
import { request } from '@wix/da-shared-react/pkg/xhr';
import snakeCase from 'lodash/snakeCase';
import { all, call, put, select, takeEvery } from 'redux-saga/effects';
import { putErrorNotification } from '@wix/da-shared-react/pkg/utils/saga';
import { API_BASE_PATH } from '../../constants';
import { runModuleInitActions } from './modules';
import {
  updateSectionLayoutVersion,
  updateModuleOrderSuccess,
  removeModuleSuccess,
  moveModule,
  addModule,
  addModuleSuccess,
  removeModule,
  addModuleFail,
} from '../actions/layout';
import { updateModule } from '../actions/modules';
import { getProfileOwnerUser } from '../selectors/users';

import {
  getInstalledModuleIds,
  getSectionLayoutVersion,
} from '../selectors/layout';
import moveArrayElement from 'lodash-move';
import { ModuleType } from '../../types/modules';
import { getModuleIdByModuleType } from '../selectors/modules';
import { getBadges } from '../selectors/aboutMe';

function* postLayout(command, data) {
  return yield call(request, {
    method: 'post',
    baseURL: API_BASE_PATH,
    url: `/layout/${command}`,
    data,
  });
}

function* hasBadges() {
  const aboutMeModuleId = yield select(
    getModuleIdByModuleType,
    ModuleType.ABOUT
  );
  const badges = yield select(getBadges, aboutMeModuleId);
  if (badges && badges.length > 0) {
    return true;
  }
  return false;
}

export function* addModuleSaga(action: ReturnType<typeof addModule>) {
  const { payload } = action;
  const { section, zone, position, moduleType: module } = payload;

  if (module === ModuleType.BADGES_SPOTLIGHT) {
    const canInstall = yield call(hasBadges);
    if (!canInstall) {
      yield all([
        putErrorNotification(
          i18next.t('widgets.badgesSpotlight.errorNotification.limit')
        ),
        put(addModuleFail(payload)),
      ]);
      return;
    }
  }

  const { username } = yield select(getProfileOwnerUser);
  const installedModulesIds = yield select(
    getInstalledModuleIds,
    section,
    zone
  );
  const afterModuleId = installedModulesIds[position - 1];
  const params = {
    username,
    page: section,
    zone: snakeCase(zone),
    after_moduleid: afterModuleId,
    module,
  };

  const response = yield call(postLayout, 'add_module', params);
  if (response && response.module) {
    yield runModuleInitActions(response.module);
    yield put(addModuleSuccess(payload));
    // augment module metadata with a special flag
    yield put(
      updateModule({
        id: response.module.id,
        moduleMeta: { isNewlyAdded: true },
      })
    );

    const newInstalledModulesIds = yield select(
      getInstalledModuleIds,
      section,
      zone
    );
    const order = newInstalledModulesIds.slice();
    order.splice(position, 0, response.module.id);
    yield put(updateModuleOrderSuccess({ section, zone, order }));
    yield put(
      updateSectionLayoutVersion({ section, version: response.layoutVersion })
    );
  } else {
    yield put(addModuleFail(payload));
  }
}

export function* removeModuleSaga(action) {
  const { section, zone, moduleId } = action.payload;
  const { username } = yield select(getProfileOwnerUser);
  const version = yield select(getSectionLayoutVersion, section);
  const params = {
    username,
    page: section,
    zone: snakeCase(zone),
    moduleid: moduleId,
    version,
  };

  const response = yield call(postLayout, 'remove_module', params);

  if (response) {
    const installedModulesIds = yield select(
      getInstalledModuleIds,
      section,
      zone
    );
    const order = installedModulesIds.filter(
      installedModuleId => installedModuleId !== moduleId
    );
    yield put(updateModuleOrderSuccess({ section, zone, order }));
    yield put(removeModuleSuccess(action.payload));
    yield put(
      updateSectionLayoutVersion({ section, version: response.layoutVersion })
    );
  }
}

export function* moveModuleSaga(action) {
  const { section, zone, moduleId, position } = action.payload;
  const { username } = yield select(getProfileOwnerUser);
  const version = yield select(getSectionLayoutVersion, section);
  const installedModulesIds = yield select(
    getInstalledModuleIds,
    section,
    zone
  );
  // imagine the drag item is pulled up and the rest items in the stack filled the gap
  const installedModulesIdsWithoutCurrent = installedModulesIds.filter(
    installedModuleId => installedModuleId !== moduleId
  );
  // now we can find out what module it will go after to be on the desired position
  const afterModuleId = installedModulesIdsWithoutCurrent[position - 1];
  const response = yield call(postLayout, 'move_module', {
    username,
    moduleid: moduleId,
    after_moduleid: afterModuleId,
    version,
  });
  if (response) {
    const currentModuleIndex = installedModulesIds.indexOf(moduleId);
    const order = moveArrayElement(
      installedModulesIds,
      currentModuleIndex,
      position
    );
    yield put(updateModuleOrderSuccess({ section, zone, order }));

    yield put(
      updateSectionLayoutVersion({ section, version: response.layoutVersion })
    );
  }
}

export default function* rootLayoutSaga() {
  yield all([
    takeEvery(addModule, addModuleSaga),
    takeEvery(removeModule, removeModuleSaga),
    takeEvery(moveModule, moveModuleSaga),
  ]);
}
